import { Box, Chip, CircularProgress, Grid2, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { BaseLegend } from "../../../components/form/legend/styled";
import { AddCollateralFormType } from "../../../types/container.type";
import { AddCollateralWrapper } from "./styled";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { BaseSelect } from "../../../components/form/select/styled";
import { BaseOption } from "../../../components/form/option/styled";
import { useEffect, useRef } from "react";
import { BaseButton } from "../../../components/button/styled";
import { retrieveCollateralIndexService } from "../../../utils/api/loanmanagement/collateral/retrieveCollateralIndex";
import Cookies from "universal-cookie";

export const AddCollateral: React.FC<AddCollateralFormType> = ({ formDetails, setFormDetails, error, isLoading, handleSubmit }) => {
    const collateralTypes = ["car", "house"];

    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        retrieveCollateralIndexService(TOKEN)
            .then((data) => {
                return setFormDetails((prev) => ({ ...prev, serialNo: data.nextIndex }));
            })
            .catch((err) => {
                console.error("Failed to fetch loan index:", err);
            });
    }, [TOKEN, setFormDetails]);

    const handleChange = (e: SelectChangeEvent<unknown> | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, type, value } = e.target as HTMLInputElement;
        if (type === 'file') {
            const input = e.target as HTMLInputElement;
            const fileArray = input.files ? Array.from(input.files) : [];
            setFormDetails(prev => ({
                ...prev,
                [name]: [...prev[name as keyof typeof prev] as File[], ...fileArray]
            }));
        } else {
            setFormDetails(prev => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleDeleteAttachment = (fileToDelete: File) => {
        setFormDetails((prev) => {
            const updated = prev.attachments.filter(file => file !== fileToDelete);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            };
            return {
                ...prev,
                attachments: updated
            };
        });
    };

    return (
        <AddCollateralWrapper
            onSubmit={handleSubmit}
        >
            <BaseLegend
                fontweight={500}
                fontsize={"18"}
                color="var(--dark-color)"
            >
                Enter required Information to add collateral
            </BaseLegend>
            <Grid2
                container
                component={"div"}
                spacing={"var(--flex-gap)"}
                className="fieldset-grid-wrapper"
            >
                <Grid2
                    size={{ mobile: 12, tablet: 6 }}
                >
                    <BaseFieldSet>
                        <BaseLabel
                            colour="var(--dark-color)"
                        >
                            Name
                        </BaseLabel>
                        <BaseInput
                            required
                            name="name"
                            value={formDetails.name}
                            placeholder="Enter First Name"
                            onChange={(e) => handleChange(e)}
                        />
                    </BaseFieldSet>
                </Grid2>
                <Grid2
                    size={{ mobile: 12, tablet: 6 }}
                >
                    <BaseFieldSet>
                        <BaseLabel
                            colour="var(--dark-color)"
                        >
                            Collateral Type
                        </BaseLabel>
                        <BaseSelect
                            required
                            name="type"
                            fontweight={400}
                            value={formDetails.type}
                            colour="var(--semi-gray-color)"
                            onChange={(e) => handleChange(e)}
                        >
                            <BaseOption
                                value={" "}
                            >
                                Select Type
                            </BaseOption>
                            {collateralTypes?.map((type: string, index: number) => {
                                return (
                                    <BaseOption
                                        key={index}
                                        value={type}
                                    >
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </BaseOption>
                                )
                            })}
                        </BaseSelect>
                    </BaseFieldSet>
                </Grid2>
                <Grid2
                    size={{ mobile: 12, tablet: 6 }}
                >
                    <BaseFieldSet>
                        <BaseLabel
                            colour="var(--dark-color)"
                        >
                            Serial Number
                        </BaseLabel>
                        <BaseInput
                            required
                            readOnly
                            name="serialNo"
                            value={formDetails.serialNo.toString().padStart(3, '0')}
                            placeholder="Serial No."
                            onChange={(e) => handleChange(e)}
                        />
                    </BaseFieldSet>
                </Grid2>
                <Grid2
                    size={{ mobile: 12, tablet: 6 }}
                >
                    <BaseFieldSet>
                        <BaseLabel
                            colour="var(--dark-color)"
                        >
                            Estimated Price
                        </BaseLabel>
                        <BaseInput
                            required
                            name="estimatedPrice"
                            value={formDetails.estimatedPrice}
                            placeholder="Enter Estimated Price"
                            onChange={(e) => handleChange(e)}
                        />
                    </BaseFieldSet>
                </Grid2>
                <Grid2
                    size={{ mobile: 12 }}
                >
                    <BaseFieldSet>
                        <BaseLabel
                            colour="var(--dark-color)"
                        >
                            Description
                        </BaseLabel>
                        <BaseInput
                            required
                            multiline
                            minRows={4}
                            name="description"
                            value={formDetails.description}
                            placeholder="Add any additional details or notes."
                            onChange={(e) => handleChange(e)}
                        />
                    </BaseFieldSet>
                </Grid2>
                <Grid2
                    size={{ mobile: 12, tablet: 6 }}
                >
                    <BaseFieldSet>
                        <BaseLabel
                            colour="var(--dark-color)"
                        >
                            Attachments
                        </BaseLabel>
                        <BaseInput
                            type="file"
                            name="attachments"
                            inputRef={fileInputRef}
                            inputProps={{
                                accept: ".jpeg,.jpg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.ppt,.pptx,.rtf,.odt,.ods,.odp",
                                multiple: true,
                            }}
                            onChange={handleChange}
                        />
                        {formDetails.attachments.length > 0 && (
                            <Stack
                                direction={"row"}
                                flexWrap={"wrap"}
                                gap={"calc(var(--flex-gap)/4)"}
                                marginBlockStart={"calc(var(--basic-margin)/4)"}
                            >
                                {formDetails?.attachments?.map((file: File, index) => (
                                    <Chip key={index} label={file.name} onDelete={() => handleDeleteAttachment(file)} />
                                ))}
                            </Stack>
                        )}
                    </BaseFieldSet>
                </Grid2>
            </Grid2>
            {error && <Typography
                fontFamily={"Inter"}
                fontWeight={"600"}
                fontSize={14}
                lineHeight={"normal"}
                color={"var(--text-salmon-pink-color)"}
                whiteSpace={"normal"}
            >
                {error}
            </Typography>}
            <Box
                overflow={"hidden"}
            >
                <BaseButton
                    type="submit"
                    variant="contained"
                    bgcolor="var(--primary-color)"
                    disableElevation
                    sx={{
                        width: { mobile: "100%", miniTablet: "auto" }
                    }}
                >
                    {isLoading ? (
                        <CircularProgress color="inherit" className="loader" />
                    ) : (
                        <Typography
                            variant={"button"}
                            fontFamily={"inherit"}
                            fontWeight={"inherit"}
                            fontSize={"inherit"}
                            lineHeight={"inherit"}
                            color={"inherit"}
                            textTransform={"inherit"}
                        >
                            Add Collateral
                        </Typography>
                    )}
                </BaseButton>
            </Box>
        </AddCollateralWrapper>
    )
}